import DiagnosticsModule, {
  MetricNames,
  MetricTags,
} from '@modules/DiagnosticsModule';
import {
  RTCSMetricNames,
  RTCSMetricTags,
  RTCSOperationNames,
  RTCSStatus,
} from '../../metrics/rtcsMetrics';
import { RtcsClient } from '@servicetitan/telecom-rtcs-client';

/**
 * Connection State Monitor
 *
 * Tracks RTCS (Real-Time Communication Service) connection state transitions and emits metrics.
 * Polls every 5 seconds to detect state changes (connected ↔ disconnected ↔ connecting).
 *
 * Metrics emitted:
 * - rtcs_state_change_total: Counter for each state transition
 * - latency_duration: How long the connection stayed in each state
 * - disconnect_duration: How long disconnections last (from disconnect → reconnect)
 *
 * Error handling: Metrics collection failures are caught and logged to prevent
 * disrupting connection monitoring (metrics are non-critical).
 */

let lastKnownState: string | null = null;
let stateChangeTime: number | null = null;
let disconnectStartTime: number | null = null;
let monitoringIntervalId: ReturnType<typeof setInterval> | null = null;
let monitoredClient: RtcsClient | null = null;

/**
 * Initializes state tracking on first poll.
 */
function initializeStateTracking(currentState: string): void {
  lastKnownState = currentState;
  stateChangeTime = Date.now();
  if (currentState === RTCSStatus.DISCONNECTED) {
    disconnectStartTime = Date.now();
  }
}

/**
 * Emits metrics for state transitions.
 */
function emitStateChangeMetrics(
  fromState: string,
  toState: string,
  duration: number,
): void {
  DiagnosticsModule.incrementCounter(RTCSMetricNames.RTCS_STATE_CHANGE_TOTAL, {
    [RTCSMetricTags.RTCS_STATE_FROM]: fromState,
    [RTCSMetricTags.RTCS_STATE_TO]: toState,
  });

  DiagnosticsModule.recordHistogram(MetricNames.LATENCY_DURATION, duration, {
    [MetricTags.OPERATION_NAME]: RTCSOperationNames.RTCS_STATE_DURATION,
    state: fromState,
  });
}

/**
 * Tracks disconnect duration and emits metrics when reconnected.
 */
function handleDisconnectTracking(currentState: string): void {
  if (
    currentState === RTCSStatus.DISCONNECTED &&
    disconnectStartTime === null
  ) {
    disconnectStartTime = Date.now();
    return;
  }

  if (currentState === RTCSStatus.CONNECTED && disconnectStartTime !== null) {
    const disconnectDuration = Date.now() - disconnectStartTime;
    if (disconnectDuration >= 0) {
      DiagnosticsModule.recordHistogram(
        MetricNames.LATENCY_DURATION,
        disconnectDuration,
        {
          [MetricTags.OPERATION_NAME]:
            RTCSOperationNames.RTCS_DISCONNECT_DURATION,
        },
      );
    }
    disconnectStartTime = null;
  }
}

/**
 * Handles state changes and emits appropriate metrics.
 */
function handleStateChange(currentState: string): void {
  if (stateChangeTime === null) {
    return;
  }

  const duration = Date.now() - stateChangeTime;
  emitStateChangeMetrics(lastKnownState!, currentState, duration);
  handleDisconnectTracking(currentState);

  lastKnownState = currentState;
  stateChangeTime = Date.now();
}

/**
 * Monitors the RTCS connection state on each interval tick.
 */
function monitorConnectionState(): void {
  if (!monitoredClient) {
    return;
  }

  const currentState = monitoredClient.state;

  if (lastKnownState === null) {
    initializeStateTracking(currentState);
    return;
  }

  if (currentState !== lastKnownState) {
    handleStateChange(currentState);
  }
}

/**
 * Starts monitoring the RTCS connection state.
 * Checks state every 5 seconds and emits metrics on state transitions.
 * @param workspaceClient - The RTCS client to monitor, or null to skip monitoring
 */
export function startConnectionStateMonitoring(
  workspaceClient: RtcsClient | null,
): void {
  if (monitoringIntervalId) {
    return;
  }

  monitoredClient = workspaceClient;
  monitoringIntervalId = setInterval(monitorConnectionState, 5000);
}

/**
 * Stops monitoring the RTCS connection state.
 * Used for cleanup during module unload or testing.
 */
export function stopConnectionStateMonitoring(): void {
  if (monitoringIntervalId) {
    clearInterval(monitoringIntervalId);
    monitoringIntervalId = null;
    lastKnownState = null;
    disconnectStartTime = null;
    monitoredClient = null;
  }
}
